import React from "react";
import { FiMapPin, FiPhone, FiEdit2, FiBookOpen } from "react-icons/fi";
import {
  MdAccountBalanceWallet,
  MdTrendingUp,
  MdReceiptLong,
  MdHistory,
  MdUndo,
  MdInventory2,
} from "react-icons/md";
import CustomerContactActions from "../Customer/CustomerContactActions";

const formatAmount = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const formatRs = (n) => `Rs ${formatAmount(n)}`;

const getInitials = (name) => {
  const parts = String(name || "?").trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return String(name || "?").slice(0, 2).toUpperCase();
};

const STAT_ITEMS = [
  {
    id: "totalBox",
    label: "Total cartons",
    desc: "Total cartons (Box)",
    valueType: "qty",
    icon: MdInventory2,
    iconClass: "text-sky-200",
    valueClass: "text-sky-100",
    cardClass: "bg-sky-400/10 border border-sky-300/20",
  },
  {
    id: "total",
    label: "Total",
    desc: "Total billed amount",
    field: "total",
    valueType: "amount",
    icon: MdReceiptLong,
    iconClass: "text-white/90",
    valueClass: "text-white",
    cardClass: "bg-white/10",
  },
  {
    id: "return",
    label: "Return",
    desc: "Return amount",
    field: "return_amount",
    valueType: "amount",
    icon: MdUndo,
    iconClass: "text-red-300",
    valueClass: "text-red-200",
    cardClass: "bg-red-400/10 border border-red-300/20",
  },
  {
    id: "opening",
    label: "Opening",
    desc: "Opening balance",
    field: "opening_balance",
    valueType: "amount",
    icon: MdHistory,
    iconClass: "text-white/70",
    valueClass: "text-white/90",
    cardClass: "bg-white/10",
  },
  {
    id: "received",
    label: "Received",
    desc: "Amount collected",
    field: "recieved",
    valueType: "amount",
    icon: MdTrendingUp,
    iconClass: "text-emerald-300",
    valueClass: "text-emerald-200",
    cardClass: "bg-white/10",
  },
  {
    id: "remaining",
    label: "Remaining",
    desc: "Still receivable",
    field: "recieveable",
    valueType: "amount",
    icon: MdAccountBalanceWallet,
    iconClass: "text-amber-200",
    valueClass: "text-amber-100",
    cardClass: "bg-amber-400/15 border border-amber-300/30",
  },
];

const ReceivableCustomerDetailHero = ({
  customer,
  totalBoxQty = 0,
  loadingBoxQty = false,
  onEdit,
}) => {
  const total = Number(customer.total || 0);
  const received = Number(customer.recieved || 0);
  const collectionPct =
    total > 0 ? Math.min(100, Math.round((received / total) * 100)) : 0;

  const getStatValue = (item) => {
    if (item.id === "totalBox") return totalBoxQty;
    return customer[item.field];
  };

  const contact1 = customer.contact != null ? String(customer.contact).trim() : "";
  const contact2 = customer.contact2 != null ? String(customer.contact2).trim() : "";

  return (
    <div className="shrink-0 rounded-2xl bg-gradient-to-br from-main via-[#127072] to-[#0a3d3e] text-white shadow-card mb-4 overflow-hidden">
      <div className="px-3.5 py-3 max720:px-3 max720:py-2.5 border-b border-white/10">
        <div className="flex items-start gap-2.5">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-white/15 border border-white/20 flex items-center justify-center text-sm font-bold">
            {getInitials(customer.name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-sm font-extrabold leading-tight truncate">
                  {customer.name}
                </h2>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                  {customer.page_no && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-violet-100">
                      <FiBookOpen size={10} />
                      Pg {customer.page_no}
                    </span>
                  )}
                  {contact1 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-white/75">
                      <FiPhone size={10} />
                      {contact1}
                    </span>
                  )}
                  {contact2 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-white/60">
                      <FiPhone size={10} />
                      {contact2}
                    </span>
                  )}
                </div>
                {customer.address && (
                  <p className="flex items-start gap-1 text-[10px] text-white/55 mt-0.5 line-clamp-1">
                    <FiMapPin size={10} className="shrink-0 mt-0.5" />
                    <span>{customer.address}</span>
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="text-[9px] uppercase tracking-wider text-white/55 font-semibold leading-none">
                  Collection
                </p>
                <p className="text-lg font-extrabold text-emerald-200 leading-tight">
                  {collectionPct}%
                </p>
              </div>
            </div>

            <div className="mt-2">
              <div className="flex justify-between text-[9px] text-white/50 mb-1 tabular-nums">
                <span>Collected {collectionPct}%</span>
                <span>
                  {formatRs(received)} / {formatRs(total)}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-300 transition-all duration-500"
                  style={{ width: `${collectionPct}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-2.5 pt-2.5 border-t border-white/10 flex flex-wrap items-center justify-between gap-2">
          <CustomerContactActions customer={customer} variant="icon" />
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-white/90 hover:text-white px-2 py-1 rounded-md bg-white/10 border border-white/20 hover:bg-white/15 transition-colors"
            >
              <FiEdit2 size={12} />
              Edit contact & page
            </button>
          )}
        </div>
      </div>

      <div className="p-2.5 max720:p-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {STAT_ITEMS.map((item) => {
            const value = getStatValue(item);
            const { id, label, desc, valueType, icon: Icon, iconClass, valueClass, cardClass } =
              item;
            return (
              <div
                key={id}
                className={`rounded-lg px-2.5 py-2 max720:px-2 max720:py-1.5 ${cardClass}`}
              >
                <div className="flex items-center gap-1">
                  <Icon size={13} className={`shrink-0 ${iconClass}`} />
                  <span className="text-[10px] font-bold uppercase tracking-wide text-white/85 leading-tight">
                    {label}
                  </span>
                </div>
                <p className="text-[9px] text-white/50 mt-0.5 leading-snug line-clamp-1">
                  {desc}
                </p>
                <p
                  className={`mt-1 text-sm max720:text-xs font-extrabold tabular-nums leading-tight ${valueClass}`}
                >
                  {valueType === "qty"
                    ? loadingBoxQty
                      ? "…"
                      : formatAmount(value)
                    : formatRs(value)}
                </p>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default ReceivableCustomerDetailHero;
